<template>
  <LoadingIndicator
    absolute
    v-if="!teiData"
  />
  <TEIDataError
    v-else-if="teiData.error || teiDataError"
    :message="teiData.error || teiDataError"
  />
  <TEILayoutContainer
    v-else
    :asset-id="assetId"
    :show-check-button="showCheckButton"
    :show-checking-button="showCheckingButton"
    :show-reset-button="showResetButton"
    :show-try-again-button="showTryAgainButton"
    :check-button-enabled="checkButtonEnabled"
    :reset-button-enabled="resetButtonEnabled"
    :scoring-bar="{
      attempts: (showFeedback || updatingAttemptItemTry) ? tries : tries + 1,
      maxAttempts: maxTries,
      score: maxScore,
      showAnswerKey: showAnswerKey,
    }"
    :show-scoring-bar="!showFeedback"
    :show-response-bar="showFeedback"
    :response-bar="{
      state: correct ? 'correct' : 'incorrect',
      content: responseBarContent,
    }"
    :is-displayed="isDisplayed"
    @reset-button-clicked="onResetClicked"
    @check-button-clicked="onCheckClicked"
    @try-again-button-clicked="onTryAgainClicked"
  >
    <div
      class="select-object-tei"
      :class="{ 'select-object-tei--portrait': imageIsPortrait }"
    >
      <TEIInstructionsCard
        position="left"
        do-sticker-position="top-right"
        class="select-object-tei__instructions-card"
        :content="promptContent"
        :max-lines="13"
      />
      <div
        class="select-object-tei__options-container"
      >
        <div
          v-if="customImage"
          class="select-object-tei__options"
          :style="optionsStyle"
        >
          <img
            class="select-object-tei__custom-image"
            :src="customImage.url"
            :width="customImage.width"
            :height="customImage.height"
          >
          <TEISelectObjectHotspot
            v-for="option in options"
            :id="option.id"
            :key="option.id"
            :accessibility-content="option.accessibility || option.text"
            :points="option.points"
            :selected="isSelected(option.id)"
            :disabled="optionIsDisabled(option.id)"
            :state="optionAnswerState(option.id)"
            :is-dirty="dirtyOptions.has(option.id)"
            class="select-object-tei__option"
            @selected-changed="onSelectedChanged"
          />
        </div>
      </div>
    </div>
  </TEILayoutContainer>
</template>

<script>
import {
  first, last, find, cloneDeep, map,
} from 'lodash-es';
import { mapActions, mapState } from 'pinia';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import TEILayoutContainer from '@/components/teis/TEILayoutContainer.vue';
import TEIInstructionsCard from '@/components/teis/TEIInstructionsCard.vue';
import TEISelectObjectHotspot from '@/components/teis/TEISelectObjectHotspot.vue';
import TEIDataError from '@/components/teis/TEIDataError.vue';
import { extractHtml } from '@/lib/utils';
import { getBoundingBox, boundingBoxesOverlap } from '@/lib/utils/grid';
import fullSlideTEI from '@/mixins/fullSlideTEI';
import modals from '@/mixins/modals';
import {
  TEI_CHECKING_ANSWERS_MODAL_DELAY,
  TEI_INSTRUCTIONS_CARD_ALLOWED_ELEMENTS,
} from '@/lib/constants';
import {
  useAssessmentStore,
  useUserStore,
} from '@/stores';
import * as types from '@/lib/constants/store';

const OPTIONS_BORDER_WIDTH = 5;
const OPTIONS_AVAILABLE_WIDTH = 668 - OPTIONS_BORDER_WIDTH;
const OPTIONS_AVAILABLE_HEIGHT = 415 - OPTIONS_BORDER_WIDTH;

/*
  Y positions for objects in the TEI data seem to be off by 9 pixels
  on the y axis. This is used to correct the y value for positions.
*/
const CUSTOM_IMAGE_POSITION_Y_OFFSET = 9;

export default {
  name: 'SelectObjectTEI',
  components: {
    LoadingIndicator,
    TEILayoutContainer,
    TEIInstructionsCard,
    TEISelectObjectHotspot,
    TEIDataError,
  },
  mixins: [fullSlideTEI, modals],
  data() {
    return {
      selections: [],
      teiDataError: null,
      checkingAnswersModalId: 'TEICheckingAnswersModal',
      updatingAttemptItemTry: false,
      dirtyOptions: new Set(),
    };
  },
  created() {
    this.selections = this.mapResponseToSelections(this.attemptItemTry?.response || []);
    this.showFeedback = !!this.attemptItemTry?.checked;
  },
  mounted() {
    this.validateTeiData();
  },
  watch: {
    teiData() {
      this.validateTeiData();
    },
    attemptItemTry(newTry) {
      if (this.isDisplayed) {
        // if we've just cleared answers
        if (this.attemptIsClearing && !newTry.checked) {
          this.submittingAttempt = false;
          this.selections = this.mapResponseToSelections(newTry.response);
          this.showFeedback = newTry.checked;

          // set attempt cleared
          this[types.SET_ATTEMPT_CLEARING](false);
        }

        // Ensure options are not dirty after submit to server.
        this.dirtyOptions.clear();
      }

      // This watcher is triggered when a response comes back from the api
      if (this.checkAnswersModal) {
        // Make sure the try number doesn't flicker between renders
        this.updatingAttemptItemTry = true;

        this.checkAnswersModal.close(() => {
          this[types.UPDATE_UNSUBMITTED_ATTEMPT](false);
          this.submittingAttempt = false;
          this.selections = this.mapResponseToSelections(newTry.response);
          this.showFeedback = newTry.checked;
          this.updatingAttemptItemTry = false;
        });
      }
    },
    selections: {
      handler() {
        if (!this.selections?.length) {
          this[types.UPDATE_UNSUBMITTED_ATTEMPT](false);
        }
      },
      deep: true,
    },
  },
  computed: {
    ...mapState(useAssessmentStore, [
      'attemptIsClearing',
    ]),
    ...mapState(useUserStore, [
      'userIsStudent',
    ]),
    tryFeedback() {
      // check multilang feedback first
      if (
        this.attemptItemTry?.multilanguage_feedback
        && this.teiLang
        && this.attemptItemTry?.multilanguage_feedback[this.teiLang]
      ) {
        return this.attemptItemTry?.multilanguage_feedback[this.teiLang];
      }

      return this.attemptItemTry?.try_feedback;
    },
    maxTries() {
      return this.attempt?.max_tries || 0;
    },
    hasTriesRemaining() {
      return !this.userIsStudent || this.tries < this.maxTries;
    },
    maxSelections() {
      let max = this.teiData?.settings?.restrictSelection
        ? Number(this.teiData?.settings?.maxAllowed)
        : this.options.length;
      if (max > this.options.length) max = this.options.length;
      return max;
    },
    showAnswerKey() {
      return this.mode === 'EDIT' && !this.userIsStudent && !!this.answerKey.length;
    },
    canSelect() {
      return this.userCanEditAttempt && !this.showAnswerKey
        && !this.showFeedback && !this.submittingAttempt;
    },
    showCheckButton() {
      return !this.showFeedback && !this.submittingAttempt;
    },
    showResetButton() {
      return !this.showFeedback && !this.submittingAttempt;
    },
    showCheckingButton() {
      return this.submittingAttempt;
    },
    showTryAgainButton() {
      return this.showFeedback && this.hasTriesRemaining && !this.correct;
    },
    checkButtonEnabled() {
      return !!this.selections.length;
    },
    resetButtonEnabled() {
      return !!this.selections.length;
    },
    scoring() {
      return this.teiData?.scoringData?.scoring;
    },
    correct() {
      return !!this.attemptItemTry?.correct;
    },
    correctAnswers() {
      return this.attemptItemTry?.correct_answers || [];
    },
    answerKey() {
      const correct = find(this.scoring, (item) => item.correct);
      if (!correct) return [];
      const response = first(correct.responses);
      if (!response) return [];
      return this.mapResponseToSelections(response);
    },
    responseBarContent() {
      if (this.tryFeedback) return this.tryFeedback;
      return this.hasTriesRemaining
        ? this.$t('Don’t worry! You will have another chance at this question.')
        : this.$t('Hm...Not quite. Check with your teacher if you have questions.');
    },
    promptContent() {
      const prompt = this.teiData?.prompt?.lang?.[this.teiLang]
        || this.teiData?.prompt?.lang?.eng;
      return extractHtml(prompt?.text, new Set(TEI_INSTRUCTIONS_CARD_ALLOWED_ELEMENTS));
    },
    options() {
      return map(this.teiData?.options, (option) => {
        const optionData = option?.lang?.[this.teiLang];
        const imageScale = this.customImage?.scale || 1;
        return {
          id: option.id,
          ...optionData,
          points: map(optionData?.position, (point) => ({
            x: (first(point)) * imageScale,
            y: (last(point) + CUSTOM_IMAGE_POSITION_Y_OFFSET) * imageScale,
          })),
        };
      });
    },
    optionsStyle() {
      if (!this.customImage) return null;
      return {
        width: `${this.customImage.width + OPTIONS_BORDER_WIDTH * 2}px`,
        height: `${this.customImage.height + OPTIONS_BORDER_WIDTH * 2}px`,
        borderWidth: `${OPTIONS_BORDER_WIDTH}px`,
      };
    },
    customImage() {
      const image = this.teiData?.uiLayout?.customImage?.lang?.[this.teiLang]?.mediaData;
      if (!image || !image.dimensions || !image.url) return null;
      const { url, dimensions } = image;
      const imageHeightWidthRatio = dimensions.height / dimensions.width;
      const optionsHeightWidthRatio = OPTIONS_AVAILABLE_HEIGHT / OPTIONS_AVAILABLE_WIDTH;
      const scale = imageHeightWidthRatio < optionsHeightWidthRatio
        ? OPTIONS_AVAILABLE_WIDTH / dimensions.width
        : OPTIONS_AVAILABLE_HEIGHT / dimensions.height;

      return {
        url,
        width: dimensions.width * scale,
        height: dimensions.height * scale,
        scale,
      };
    },
    imageIsPortrait() {
      return !!this.customImage && (this.customImage?.width < this.customImage?.height);
    },
    hasOverlappingOptions() {
      return this.options.some((option) => this.options
        .filter((op) => op.id !== option.id)
        .map((op) => getBoundingBox(op.points))
        .find((box) => boundingBoxesOverlap(box, getBoundingBox(option.points))));
    },
  },
  methods: {
    ...mapActions(useAssessmentStore, [
      types.SUBMIT_RESPONSE,
      types.UPDATE_UNSUBMITTED_ATTEMPT,
      types.SET_ATTEMPT_CLEARING,
    ]),
    validateTeiData() {
      if (this.teiData) {
        const uiLayout = this.teiData?.uiLayout;

        if (this.teiData?.settings?.productType !== 'science-techbook') {
          this.teiDataError = this.$t('Error: TEI product type must be science-techbook.');
          return;
        }

        if (this.teiData?.scoringMode !== 'machine-scored') {
          this.teiDataError = this.$t('Error: Scoring mode should be machine-scored.');
          return;
        }

        const backgroundImage = uiLayout?.backgroundImage?.lang[this.teiLang]?.mediaData;
        if (backgroundImage) {
          this.teiDataError = this.$t('Error: Background images are not supported. Use custom image instead.');
          return;
        }

        const customImage = uiLayout?.customImage?.lang[this.teiLang]?.mediaData;
        if (!customImage) {
          this.teiDataError = this.$t('Error: A custom image is required for this TEI.');
          return;
        }

        if (customImage.type !== 'image') {
          this.teiDataError = this.$t('Error: Custom image must be image media type.');
          return;
        }

        if (uiLayout?.type !== 'custom') {
          this.teiDataError = this.$t('Error: TEI layout type must be custom.');
          return;
        }

        if (uiLayout?.customImageAlignment !== 'top-left') {
          this.teiDataError = this.$t('Error: Custom image alignment must be top-left.');
        }

        if (!uiLayout?.defineRegionLayout) {
          this.teiDataError = this.$t('Error: Only custom shaped objects are supported.');
        }

        if (this.hasOverlappingOptions) {
          this.teiDataError = this.$t('Error: Options cannot overlap each other.');
        }
      }
    },
    onResetClicked() {
      this.selections = [];
      this.dirtyOptions.clear();
    },
    onCheckClicked() {
      this.checkAnswersModal = this.openModalDeferred(TEI_CHECKING_ANSWERS_MODAL_DELAY, {
        id: this.checkingAnswersModalId,
        type: this.checkingAnswersModalId,
      });
      this.submittingAttempt = true;
      this[types.SUBMIT_RESPONSE]({
        teiId: this.assetId,
        payload: this.mapSelectionsToResponse(this.selections),
      });
    },
    onTryAgainClicked() {
      this.selections = [];
      this.showFeedback = false;
      this.dirtyOptions.clear();
    },
    onSelectedChanged({ id }) {
      const selections = cloneDeep(this.selections);
      const isSelected = selections.includes(id);
      if (isSelected) {
        this[types.UPDATE_UNSUBMITTED_ATTEMPT](true);
        this.selections = selections.filter((sel) => sel !== id);
        this.dirtyOptions.add(id);
        return;
      }
      if (this.selections.length >= this.maxSelections) {
        this.selections = selections;
        return;
      }
      selections.push(id);
      this[types.UPDATE_UNSUBMITTED_ATTEMPT](true);
      this.selections = selections;
      this.dirtyOptions.add(id);
    },
    isSelected(id) {
      return this.selections.includes(id);
    },
    optionAnswerState(id) {
      if (this.showAnswerKey) {
        return this.answerKey.includes(id) ? 'correct' : 'incorrect';
      }
      if (this.showFeedback && this.selections.includes(id)) {
        /*
          If the try is not completely correct, then show all the selections
          as incorrect. This is so the correct answers cannot be accumulated
          over several tries.
        */
        return this.correct ? 'correct' : 'incorrect';
      }
      return null;
    },
    optionIsDisabled(id) {
      return (
        this.canSelect && this.selections.length >= this.maxSelections
        && !this.isSelected(id)
      ) || !this.canSelect;
    },
    mapResponseToSelections(response) {
      return cloneDeep(response?.objects) || [];
    },
    mapSelectionsToResponse(selections) {
      return {
        objects: selections,
      };
    },
  },
};
</script>

<style lang="stylus">
.select-object-tei {
  display: flex;
  height: 100%;
  padding: 20px;
  padding-inline-start: 0;
  width: 100%;

  &__instructions-card {
    flex: 0 0 auto;
    padding: 22px $nebula-space-3x $nebula-space-2x $nebula-space-3x;
  }

  &__options-container {
    align-items: center;
    display: flex;
    flex: 1 1 auto;
    justify-content: center;
    padding: 0 $nebula-space-1x $nebula-space-4x $nebula-space-7x;
  }

  &__options {
    background: $nebula-color-white;
    border-color: $nebula-color-white;
    border-radius: $nebula-border-radius-default;
    border-style: solid;
    position: relative;
  }

  &__custom-image {
    border-radius: $nebula-border-radius-small + $nebula-space-half;
  }

  &--portrait {
    .select-object-tei__options-container {
      padding-left: $nebula-space-1x;
    }
  }
}
</style>
